import { ConfigProvider, Input, message, Select, theme } from 'antd';
import React, { useState } from 'react';

import { onboardingCheckName, onboardingSetupProfile } from '@/services/Organization';

interface Step0ProfileProps {
  onNext: (data: { name: string; org_type: string; gst_number?: string }) => void;
  loading?: boolean;
}

const ORG_TYPES = [
  { label: 'Private Limited', value: 'Private Limited' },
  { label: 'Public Limited', value: 'Public Limited' },
  { label: 'Partnership', value: 'Partnership' },
  { label: 'LLP', value: 'LLP' },
  { label: 'Sole Proprietorship', value: 'Sole Proprietorship' },
];

const Step0Profile: React.FC<Step0ProfileProps> = ({ onNext, loading: parentLoading }) => {
  const [name, setName] = useState('');
  const [orgType, setOrgType] = useState<string | undefined>(undefined);
  const [gstNumber, setGstNumber] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [checkingName, setCheckingName] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleNameBlur = async () => {
    if (!name.trim()) return;
    setCheckingName(true);
    setNameError(null);
    try {
      const res = await onboardingCheckName(name.trim());
      if (res && res.success === false) {
        setNameError(res.message || 'Organization name is not available.');
      } else {
        setNameError(null);
      }
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || err?.message;
      if (errMsg) {
        setNameError(errMsg);
      }
    } finally {
      setCheckingName(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError('Please enter your organization name.');
      return;
    }
    if (!orgType) {
      message.warning('Please select your organization type.');
      return;
    }

    setSubmitting(true);
    setNameError(null);

    try {
      // First check name
      const checkRes = await onboardingCheckName(name.trim());
      if (checkRes && checkRes.success === false) {
        setNameError(checkRes.message || 'Organization name is not available.');
        setSubmitting(false);
        return;
      }

      // Call setup profile API
      await onboardingSetupProfile({
        name: name.trim(),
        org_type: orgType,
        gst_number: gstNumber.trim() || undefined,
      });

      onNext({
        name: name.trim(),
        org_type: orgType,
        gst_number: gstNumber.trim() || undefined,
      });
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || err?.message;
      if (errMsg && errMsg.toLowerCase().includes('name')) {
        setNameError(errMsg);
      } else {
        // Continue even if network simulated
        onNext({
          name: name.trim(),
          org_type: orgType,
          gst_number: gstNumber.trim() || undefined,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isLoading = parentLoading || submitting || checkingName;

  return (
    <ConfigProvider
      theme={{
        algorithm: [theme.darkAlgorithm],
        token: {
          colorPrimary: '#FF5612',
          colorBgContainer: '#141414',
          colorBorder: '#262626',
          borderRadius: 12,
        },
      }}
    >
      <div className='w-full max-w-md mx-auto flex flex-col items-center text-center'>
        <h2 className='text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-2'>
          Set up your business profile
        </h2>
        <p className='text-xs sm:text-sm text-grayZl-400 mb-8'>
          Add your organization details to get started with ZebraLearn Business.
        </p>

        <form onSubmit={handleSubmit} className='w-full text-left space-y-5'>
          {/* Organization Name */}
          <div>
            <label className='block text-xs font-semibold text-grayZl-300 mb-2'>
              Organization Name <span className='text-[#FF5612]'>*</span>
            </label>
            <Input
              size='large'
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              onBlur={handleNameBlur}
              placeholder='Enter organization name'
              className={`!bg-[#141414] !h-12 !text-sm !text-white !rounded-xl placeholder:!text-grayZl-600 focus:!border-[#FF5612] ${
                nameError ? '!border-red-500' : '!border-[#262626]'
              }`}
            />
            {nameError && (
              <p className='text-[11px] text-red-500 mt-1 font-medium'>{nameError}</p>
            )}
          </div>

          {/* Organization Type */}
          <div>
            <label className='block text-xs font-semibold text-grayZl-300 mb-2'>
              Organization Type <span className='text-[#FF5612]'>*</span>
            </label>
            <Select
              size='large'
              value={orgType}
              onChange={setOrgType}
              placeholder='Select organization type'
              className='w-full !h-12'
              options={ORG_TYPES}
              dropdownStyle={{
                backgroundColor: '#141414',
                borderColor: '#262626',
                borderRadius: 12,
              }}
            />
          </div>

          {/* GST Number */}
          <div>
            <label className='block text-xs font-semibold text-grayZl-300 mb-2'>
              GST Number (Optional)
            </label>
            <Input
              size='large'
              value={gstNumber}
              onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
              placeholder='Enter GSTIN'
              maxLength={15}
              className='!bg-[#141414] !border-[#262626] !h-12 !text-sm !text-white !rounded-xl placeholder:!text-grayZl-600 focus:!border-[#FF5612]'
            />
          </div>

          <p className='text-[11px] text-grayZl-500 text-center pt-2'>
            You can always change your profile settings later.
          </p>

          {/* Submit Button */}
          <button
            type='submit'
            disabled={isLoading}
            className='w-full h-12 bg-[#FF5612] hover:bg-[#E54D10] disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-all duration-150 flex items-center justify-center gap-2 shadow-lg cursor-pointer active:scale-98'
          >
            <span>{isLoading ? 'Checking & Saving...' : 'Continue'}</span>
            <span>&rarr;</span>
          </button>
        </form>
      </div>
    </ConfigProvider>
  );
};

export default Step0Profile;
